
"use client"

import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface ComboboxProps {
  options: { value: string; label: string }[]
  value?: string
  onChange: (value: string) => void
  placeholder: string
  emptyMessage?: string
  className?: string
  searchPlaceholder?: string
  disabled?: boolean
}

export function Combobox({
  options = [], 
  value,
  onChange,
  placeholder,
  emptyMessage = "No results found.",
  className,
  searchPlaceholder,
  disabled = false,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  
  // Ensure options is always an array and stable across renders
  const safeOptions = React.useMemo(() => 
    Array.isArray(options) ? options : [],
    [options]
  );

  // Filter options based on input value - only recalculate when inputValue or safeOptions change
  const filteredOptions = React.useMemo(() => {
    if (!inputValue) return safeOptions;
    return safeOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [safeOptions, inputValue]);

  // Find selected option - only recalculate when value or safeOptions change
  const selectedOption = React.useMemo(() => 
    safeOptions.find((option) => option.value === value),
    [safeOptions, value]
  );

  // Stable function reference
  const handleSelect = React.useCallback((currentValue: string) => {
    onChange(currentValue);
    setOpen(false);
    setInputValue("");
  }, [onChange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-sans text-sm bg-white dark:bg-gray-950", className)}
          disabled={disabled}
        >
          {selectedOption?.label || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white dark:bg-gray-950">
        <Command>
          <CommandInput 
            placeholder={searchPlaceholder || `Search ${placeholder.toLowerCase()}...`}
            value={inputValue}
            onValueChange={setInputValue}
            className="font-sans"
          />
          <CommandList>
            <CommandEmpty className="py-2 text-sm text-muted-foreground font-sans">{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                  className="font-sans"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 text-primary",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
